import React, { useEffect, useState, useRef  } from 'react';
import Select from 'react-select';
import axios from 'axios';
import TextInput from '../../TextInput';
import GradeInput from '../../GradeInput';
import styles from '../../HomePageForm/HomePageForm.module.sass';
import useSchools from '../../../hooks/useSchools';
import Button from '../../Button';
import useTeachers from '../../../hooks/useTeachers';

const customStyles = {
  option: (provided, state) => ({
    ...provided,
    color: state.isSelected ? '#000' : '#777',
    fontFamily: 'inherit',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#777',
    fontFamily: 'inherit',
    fontSize: '0.95em',
  }),
};

const StudentFields = ({ student, index, updateStudent, removeStudent, isElementary, teachers, availableGrades }) => (
  <div className="row">
    <div className="col-md-8 col-md-offset-2">
      <div className="well" key={index}>
        <div className="row">
          <div className="col-xs-12">
            <label style={{ marginBottom: '1em', display: 'block' }}>
              Student First Name
              <TextInput
                val={student.firstName}
                onChange={(e) => updateStudent(index, 'firstName', e.target.value)}
              />
            </label>
          </div>
          <div className="col-xs-12">
            <label style={{ marginBottom: '1em', display: 'block' }}>
              Student Last Name
              <TextInput
                val={student.lastName}
                onChange={(e) => updateStudent(index, 'lastName', e.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <label style={{ marginBottom: '1em', display: 'block' }}>
              Grade
              <GradeInput
                val={student.grade}
                onChange={(e) => updateStudent(index, 'grade', e)}
                availableGrades={availableGrades}
              />
            </label>
          </div>
          <div className="col-xs-12">
            <label style={{ marginBottom: '1em', display: 'block' }}>
              Student ID
              <TextInput
                val={student.studentId}
                onChange={(e) => updateStudent(index, 'studentId', e.target.value)}
              />
            </label>
          </div>
        </div>
        {isElementary && student.grade !== 'STAFF' && (
          <div>
            <label style={{ fontSize: '0.8em', marginLeft: '14px', marginBottom: '-20px' }}>Teacher's Name</label>
            <Select
              styles={customStyles}
              options={teachers.map((teacherName) => ({ value: teacherName, label: teacherName }))}
              onChange={(e) => updateStudent(index, 'teacher', e.value)}
              value={student.teacher ? { value: student.teacher, label: student.teacher } : null}
            />
          </div>
        )}
        {index > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <Button fullWidth variant="error" onClick={() => removeStudent(index)} type="button">Remove Student</Button>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default function Registration() {
  const defaultSchool = localStorage.getItem('REGISTRATION-SCHOOL');
  const [isRetake, setIsRetake] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [studentSchool, setStudentSchool] = useState(defaultSchool ? JSON.parse(defaultSchool) : ({}));
  const [students, setStudents] = useState([{ firstName: '', lastName: '', grade: '', studentId: '', teacher: '' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const { data: teachers } = useTeachers({ schoolId: studentSchool?.value });
  const [availableGrades, setAvailableGrades] = useState([]);
  const [registrationResults, setRegistrationResults] = useState([]);
  const checkboxRef = useRef(null);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('REGISTRATION-EMAIL');
    const savedPhone = localStorage.getItem('REGISTRATION-PHONE');
    const urlParams = new URLSearchParams(window.location.search);
    const retakeParam = urlParams.get('retake');

    if (savedEmail) setEmailAddress(savedEmail);
    if (savedPhone) setPhoneNumber(savedPhone);
    if (retakeParam === 'true') setIsRetake(true);
  }, []);

  useEffect(() => {
    if (studentSchool?.value) {
      axios.get(`schools/${studentSchool.value}/grades`)
        .then((response) => {
          setAvailableGrades(response.data);
        })
        .catch((error) => {
          console.error('Failed to fetch available grades', error);
          setAvailableGrades([]); // Reset to empty array on error
        });
    } else {
      setAvailableGrades([]);
    }
  }, [studentSchool]);

  useEffect(() => {
    if (isCheckboxChecked) {
      // Do something when the checkbox is checked
      console.log('Checkbox is checked!');
      // Your logic here
    }
  }, [isCheckboxChecked]);

  const isElementary = studentSchool?.schoolTypeId === 3;

  const saveRegistrationData = () => {
    localStorage.setItem('REGISTRATION-SCHOOL', JSON.stringify(studentSchool));
    localStorage.setItem('REGISTRATION-EMAIL', emailAddress);
    localStorage.setItem('REGISTRATION-PHONE', phoneNumber);
  };

  const schools = useSchools();

  const handleSubmit = (e) => {
    e.preventDefault();

    setIsLoading(true);
    setIsSuccessful(false);
    setErrors([]);
    setRegistrationResults([]);

    const registrationData = {
      registrations: students.map((student) => ({
        customer_registration: {
          email: emailAddress,
          phone: phoneNumber,
        },
        student: {
          student_id: student.studentId,
          first_name: student.firstName,
          last_name: student.lastName,
          grade: student.grade,
          teacher: student.teacher,
        },
      })),
      school_id: studentSchool.value,
      is_retake: isRetake,
    };

    axios({
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      method: 'post',
      url: '/customer_registrations',
      responseType: 'json',
      baseURL: '/',
      data: registrationData,
    })
      .then((response) => {
        saveRegistrationData();

        // Check for redirect URL
        if (response.data.redirect) {
          window.location.href = response.data.redirect;
          return;
        }

        if (response.data.errors && response.data.errors.length > 0) {
          setErrors(response.data.errors);
        }

        if (response.data.results && response.data.results.length > 0) {
          setIsSuccessful(true);
          const successfulRegistrations = response.data.results.length;
          setRegistrationResults(response.data.results);
          setStudents(students.slice(successfulRegistrations));
        } else if (!response.data.errors || response.data.errors.length === 0) {
          setIsSuccessful(true);
          setStudents([{ firstName: '', lastName: '', grade: '', studentId: '', teacher: '' }]);
        }
      })
      .catch((error) => {
        if (error.response) {
          if (error.response.data) {
            setErrors(error.response.data.errors || ['An error occurred. Please try again.']);
          } else {
            setErrors(['A server error occurred. Please try again later.']);
          }
        } else if (error.request) {
          setErrors(['No response received from the server. Please try again.']);
        } else {
          console.error('An unexpected error occurred', error);
          setErrors(['An unexpected error occurred. Please try again.']);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const addStudent = (e) => {
    e.preventDefault();
    setStudents([...students,
      { firstName: '', lastName: '', grade: '', studentId: '', teacher: '' }
    ]);
  };

  const removeStudent = (index) => {
    setStudents(students.filter((_, i) => i !== index));
  };

  const updateStudent = (index, field, value) => {
    const updatedStudents = [...students];
    updatedStudents[index][field] = value;
    setStudents(updatedStudents);
  };

  const renderResults = () => {
    if (registrationResults.length <= 1) return null;

    return (
      <div className="panel panel-success">
        <div className="panel-heading">You have registered for updates for the following student(s):</div>
        <div className="panel-body">
          <ul className="list-group">
            {registrationResults.map((result, index) => (
              <li key={index} className="list-group-item" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>
                    {result.customer_registration.student.first_name} {result.customer_registration.student.last_name}
                  </strong>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-muted" style={{ marginTop: '1rem', fontStyle: 'italic' }}>
            We will send you a link to their pictures shortly after picture day.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      {isSuccessful && (
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <div style={{ textAlign: 'center', color: 'rgb(14, 57, 7)', backgroundColor: 'rgb(162, 215, 158)', border: '1px solid rgb(94, 176, 71)', borderRadius: '8px', padding: '1em', margin: '1em 0', }}>
              Thank you for registering your child. We will send you a link to their pictures shortly after picture day.
            </div>
          </div>
        </div>
      )}
      {renderResults()}
      {errors.length > 0 && (
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <div style={{ color: '#9f2828', backgroundColor: '#ffcfcf', border: '1px solid #9f2828', borderRadius: '8px', padding: '1em', margin: '1em 0', textAlign: 'center' }}>
              <ul>
                {errors.map((error, index) => <li key={index}>{error}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
      <div className="row">
        <div className="col-md-8 col-md-offset-2">
          <h1 style={{ textAlign: 'center', marginBottom: '1.5em', fontSize: '1.1em', fontWeight: 'bold' }}>
            {isRetake
              ? 'Your school pictures matter! Lets make it right. Sign Up for Retake Day.'
              : "Register to receive text and email updates when your student's pictures are available!"}
          </h1>
          <p style={{ fontStyle: 'italic', color: '#777', fontSize: '.9rem', textAlign: 'center', marginBottom: '1rem', lineHeight: '1.25rem' }}>
            By registering, the user affirms that they are the legal guardian or
            close relative of the student whose image(s)
            they are requesting and that they authorize Shoob Photography to print and
            mail portraits of them to their home address.
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <div className="well">
              <div className="row">
                <div className="col-md-12">
                  <label htmlFor="school-input" style={{ marginBottom: '1em', display: 'block' }}>
                    School Name
                    <Select
                      id="school-input"
                      defaultValue={studentSchool}
                      options={schools.map((selectSchool) => (
                        ({ value: selectSchool.id, label: selectSchool.name, schoolTypeId: selectSchool.schoolTypeId })
                      ))}
                      className={styles.schoolInput}
                      styles={customStyles}
                      onChange={setStudentSchool}
                    />
                  </label>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <label style={{ marginBottom: '1em', display: 'block' }}>
                    Contact Email Address
                    <TextInput val={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} />
                  </label>
                </div>
                <div className="col-md-12">
                  <label style={{ display: 'block' }}>
                    Contact Phone Number
                    <TextInput val={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <div className="checkbox">
              <label>
              <input
                type="checkbox"
                id="terms-and-conditions"
                ref={checkboxRef}
                required
                onChange={() => setIsCheckboxChecked(checkboxRef.current.checked)}
              /> I agree to receive recurring automated marketing and informational text messages from Shoob Photography at the mobile number provided — including notifications when my student's photos are ready to view, order/shipping/delivery updates, and occasional promotions and special offers. Consent is not a condition of any purchase. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for help. See our <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and <a href="/guarantee#sms-terms" target="_blank" rel="noopener noreferrer">Messaging Terms</a>.
              </label>
            </div>
          </div>
        </div>
        {students.map((student, index) => (
          <StudentFields
            key={index}
            student={student}
            index={index}
            updateStudent={updateStudent}
            removeStudent={removeStudent}
            isElementary={isElementary}
            teachers={teachers}
            availableGrades={availableGrades}
          />
        ))}
        <div className="row">
          <div className="col-md-8 col-md-offset-2">
            <Button variant="submit" fullWidth disabled={isLoading || students.some(student => !student.studentId) || (document.getElementById('terms-and-conditions') && !document.getElementById('terms-and-conditions').checked)}>Register</Button>
            
            <Button
              variant="primary"
              fullWidth
              onClick={addStudent}
              style={{ marginBottom: '1rem', marginTop: '1rem' }}
              type="button"
            >
              Add Another Student
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
